/**
 * One weekday column: a header, and the card box under it holding either a workout or the "+"
 * tile — as `Builder planu - tydzien - programator.dc.html` draws it.
 *
 * The canon's first note is the rule this file exists to obey:
 *
 * > Poniedziałek–niedziela stoją zawsze w tym samym miejscu … Karta sesji siedzi w gnieździe pod
 * > nagłówkiem — to ona jest przenoszalna.
 *
 * The header belongs to the WEEK, not to the training. So the column always renders, the header
 * never moves, and an unprogrammed day is a visible empty box rather than a missing column.
 *
 * ── The card BOX moved here, and the two layers inside it cross-fade ─────────────────────────
 * The mock draws one box per column and puts two absolutely-positioned layers in it: `emptySt`, the
 * `+` tile, and `bodySt`, the workout. The box owns the fill, the edge, the radius, the shadow and
 * every state; the workout inside it is transparent. So this component owns the box and
 * `SessionCard` renders the body — which is what makes adding a day a cross-fade rather than a cut.
 *
 * **The fade only runs one way, and that is a stated limit.** Adding a day mounts the body layer and
 * the two cross-fade exactly as the mock does. REMOVING one unmounts the body, so it disappears in
 * the frame the store answers rather than fading out over 300ms. Keeping a removed day mounted long
 * enough to fade it would mean a timer standing between a store operation and the picture, which is
 * the bodge this codebase does not write. Flagged rather than approximated.
 *
 * ── The box is the drop target AND the drag source ───────────────────────────────────────────
 * The mock puts `draggable` on `cardSt` and `onDrop` on the column, and one element can be both. The
 * ref is created here and handed to `SessionCard`, which is still what calls `useDraggableItem` —
 * the payload is the DAY's and the card itself starts the gesture.
 *
 * An occupied target SWAPS the two days' weekdays — see `moveDayToWeekday` for why refusing instead
 * would make a fully programmed week the one week nothing can be rearranged in. A refused target
 * simply never lights up: `canDrop` returns false, so the adapter does not enter the element and no
 * indicator paints (view ADR §11).
 */

import { useId, useRef } from 'react';

import { previewMoveCommand } from '~/entities/plan-draft';
import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import type { AddDayRefusalView } from '../model/builderChrome';
import type { WeekdayColumnView } from '../model/weekViewModel';

import { SessionCard, type DayCardActions, type SessionMoveProps } from './SessionCard';
import styles from './DayColumn.module.css';

interface DayColumnProps {
  readonly column: WeekdayColumnView;
  /** Open the day dock on a day. `null` when the plan cannot be edited. */
  readonly onProgramDay: ((slotToken: string) => void) | null;
  /**
   * Create a training day on this empty weekday and open the dock on it. `null` when the plan cannot
   * be edited, or when this weekday already has one.
   */
  readonly onAddDay: ((weekday: number) => void) | null;
  /** The day whose card should take focus back after the dock closed. See `useDayDock`. */
  readonly returnFocusToken: string | null;
  /** "+ Trening" — the tile's accessible name, which contains the word it draws. */
  readonly addDayLabel: string;
  /**
   * Why no training may be added to this phase, or `null`. The tile stays, refused, and says so.
   *
   * It is drawn HERE, under the tile that refuses, rather than in a row above the canvas (owner,
   * 16 September 2026). Two words fit an 80px column; the whole sentence reaches the tooltip, the
   * description and the announcement, where there is room for it. See `AddDayRefusalView`.
   */
  readonly addDayRefusal: AddDayRefusalView | null;
  /** "Trening" — what the tile actually prints, under the mock's `+` square. */
  readonly addDayTileLabel: string;
  /** Forwarded to the card, unchanged. */
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  /** The move capability, forwarded unchanged. `null` on a plan that cannot be edited. */
  readonly move: SessionMoveProps | null;
  /** Duplicate and delete, forwarded unchanged. `null` on a plan that cannot be edited. */
  readonly actions: DayCardActions | null;
  /** True when a KEYBOARD lift somewhere in the week is currently pointed at this column. */
  readonly isPendingTarget: boolean;
  /**
   * True when the day panel is open ON THIS DAY — the mock's `active`, which paints the box's edge
   * in the accent and adds a 3px ember ring. The mock's own `this.state.sel === i`.
   */
  readonly isSelected: boolean;
}

export function DayColumn({
  column,
  onRenameSession,
  onProgramDay,
  onAddDay,
  returnFocusToken,
  addDayLabel,
  addDayRefusal,
  addDayTileLabel,
  move,
  actions,
  isPendingTarget,
  isSelected,
}: DayColumnProps) {
  // The box, not the column: the mock puts the target ring and the drag on `cardSt`, and the header
  // is deliberately outside it — "nagłówek NIE należy do sesji".
  const boxRef = useRef<HTMLDivElement>(null);
  /* Per column, because the sentence is now per column. The id and the text are rendered by the
   * same component, so they cannot drift apart. */
  const reasonId = useId();
  const drop = useDropTarget({
    ref: boxRef,
    data: { kind: 'weekday', weekday: column.weekday },
    // Two sources land on a column, and the gate for each is the entity's own — asked here so a
    // refused column never lights up (view ADR §11: "no indicator, no cursor change").
    canDrop: (source) => {
      if (move === null) return false;

      // A SESSION card. `unchanged` is not a refusal: releasing a card back on its own column is a
      // legal drop that does nothing, and greying it out would put a dead zone around every card a
      // trainer picked up.
      if (source.kind === 'session') {
        const daySlotToken = source.daySlotToken;
        return (
          typeof daySlotToken === 'string' &&
          previewMoveCommand({
            kind: 'moveDayToWeekday',
            daySlotToken,
            targetWeekday: column.weekday,
          }).kind !== 'refused'
        );
      }

      // An EXERCISE dragged out of the open dock — mechanic 3. A rest day has no session to receive
      // it, and creating one here would put a workout on the client's calendar as a side effect of
      // a mis-aimed gesture; "+ Trening" is the act that creates a day.
      if (source.kind === 'exercise') {
        const exerciseSlotToken = source.exerciseSlotToken;
        const targetDaySlotToken = column.day?.slotToken;
        return (
          typeof exerciseSlotToken === 'string' &&
          targetDaySlotToken !== undefined &&
          previewMoveCommand({
            kind: 'moveExercise',
            exerciseSlotToken,
            targetDaySlotToken,
            // The append index. A week card draws no positions to land between, so deriving one from
            // the pointer's height over it would be a guess dressed as precision.
            targetBlockIndex: column.day?.blocks.length ?? 0,
          }).kind !== 'refused'
        );
      }

      return false;
    },
  });

  const isTarget = drop.isOver || isPendingTarget;
  const hasDay = column.day !== null;

  return (
    <div
      className={`${String(styles.column)} ${String(hasDay ? styles.training : styles.rest)}`}
      role="listitem"
      // The address a later unit measures a column against — it scrolls an opened day out from under
      // the 574px panel, and a weekday index would name a position rather than a workout.
      data-day-slot={column.day?.slotToken}
      data-target={isTarget ? '' : undefined}
    >
      <div className={styles.head}>
        {/* Two letters drawn, the full name spoken. The mock heads each column "PN WT ŚR CZ PT SO
         * ND"; a screen reader announcing "PN" would be reading an abbreviation nothing on the page
         * expands, so the full weekday stands beside it, visually hidden. */}
        <span className={styles.weekday}>
          <span aria-hidden="true">{column.weekdayShortLabel}</span>
          <span className={visuallyHidden.srOnly}>{column.weekdayLabel}</span>
        </span>
        {/* Nothing at all when the session carries no authored estimate — never "~0 min". The element
         * is still emitted so the header keeps its two-line height whether or not the figure is
         * there, which is what stops a week of mixed days drawing a ragged top edge. */}
        <span className={styles.duration}>{column.duration ?? ''}</span>
      </div>

      <div ref={boxRef} className={styles.card} data-selected={isSelected ? '' : undefined}>
        {/* The empty layer, mounted in every state so the two can cross-fade. `inert` once a workout
         * stands over it: it is drawn at zero opacity and must take neither focus nor a press. */}
        {/* The layer holds NOTHING once a workout stands over it.
         *
         * It stayed mounted so the two could cross-fade, and its contents stayed with it — so a
         * programmed column carried a second, invisible "Przerwa" behind its card. Invisible and
         * `inert` reaches no trainer, but it is still in the document: `getByText('Przerwa')` on a
         * week of four trainings and three rest days counted seven. A word that contradicts the
         * card on top of it should not be in the tree at all, whoever is reading. The BOX stays,
         * because the box is what fades. */}
        <div className={styles.empty} inert={hasDay ? true : undefined}>
          {hasDay ? null : onAddDay === null ? (
            // Read-only: no tile, because there is no act behind it. The word the canon puts on an
            // unprogrammed weekday stands on its own.
            <span className={styles.emptyLabel}>{column.emptyLabel}</span>
          ) : (
            <>
              {/* "Przerwa", present to assistive technology and drawn nowhere.
               *
               * A "+ Trening" button is an affordance, not a statement, so on an editable week the
               * day's actual state — empty — would otherwise be conveyed by nothing but a narrow
               * dashed box. It is hidden rather than drawn because the mock draws no third line in
               * an 84px strip. Sighted trainers read the empty dashed column; everyone else reads
               * this. */}
              <span className={visuallyHidden.srOnly}>{column.emptyLabel}</span>
              <button
                type="button"
                className={styles.addDay}
                // The fuller name, which CONTAINS the visible word: seven identical "Trening"
                // buttons would be seven controls a screen-reader user cannot tell apart by name.
                aria-label={addDayLabel}
                /* Refused, the tile keeps its place in the tab order and points at the one sentence
                 * above the canvas rather than repeating it in seven columns (view ADR §12). It is
                 * `aria-disabled`, never the attribute, for the same reason. */
                aria-disabled={addDayRefusal === null ? undefined : 'true'}
                aria-describedby={addDayRefusal === null ? undefined : reasonId}
                title={addDayRefusal?.full}
                onClick={() => {
                  if (addDayRefusal !== null) {
                    /* A refused control that answers a press with nothing reads as a broken one —
                     * and this tile is drawn as a tile either way, so a trainer who has not read
                     * the sentence above the canvas has no other signal. The reason goes into the
                     * canvas's own live region, which is where every other refusal on this surface
                     * is spoken. `aria-describedby` already carries it for a reader who lands on
                     * the tile; this is for the one who presses it. */
                    move?.announce(addDayRefusal.full);
                    return;
                  }
                  onAddDay(column.weekday);
                }}
              >
                <span className={styles.emptyTile} aria-hidden="true">
                  +
                </span>
                <span className={styles.emptyLabel}>{addDayTileLabel}</span>
              </button>
              {/* The reason, at the control. Two words drawn — an 80px column holds no more — and
                * the whole sentence spoken: the short form would be a label a screen reader could
                * not act on, and `title` reaches a pointer and nothing else. */}
              {addDayRefusal !== null && (
                <>
                  <span className={styles.refusal} aria-hidden="true">
                    {addDayRefusal.short}
                  </span>
                  <span id={reasonId} className={visuallyHidden.srOnly}>
                    {addDayRefusal.full}
                  </span>
                </>
              )}
            </>
          )}
        </div>

        {column.day !== null && (
          <SessionCard
            day={column.day}
            box={boxRef}
            onRename={onRenameSession}
            onProgram={onProgramDay}
            shouldTakeFocus={returnFocusToken === column.day.slotToken}
            move={move}
            actions={actions}
          />
        )}
      </div>
    </div>
  );
}
